<template>
   <dialog
      ref="cookiesPopupHTMLElem"
      data-cy="cookie-manager-dialog"
      class="body-font fixed bottom-0 top-auto z-100 w-full max-w-full rounded-md border border-grey-30 bg-white p-7 sm:bottom-auto sm:top-1/2 sm:mx-auto sm:w-[440px] sm:-translate-y-1/2 [&::backdrop]:bg-backdrop"
   >
      <h2 class="h4 mb-4">{{ $t("privacy-and-terms.cookie_manager_mini.heading") }}</h2>
      <RichTextRenderer :document="description.content" class="text-sm" />

      <!-- Privacy button options (reject, accept, manage) -->
      <div class="mb-3 mt-8">
         <div>
            <!-- accept all cookies -->
            <DefaultButton
               data-cy="cookie-accept-all"
               class="w-full"
               autofocus
               size="sm"
               :color="Theme.BrandDark"
               @click="onChoiceClick('accept-all')"
            >
               <span class="px-3">{{ $t("privacy-and-terms.cookie_manager_mini.button_accept_all") }}</span>
            </DefaultButton>
         </div>

         <div class="mt-4 flex flex-col sm:flex-row">
            <!-- customise cookies -->
            <DefaultButton
               data-cy="cookie-manage"
               class="shrink-0 grow"
               size="sm"
               :color="Theme.BrandDark"
               styleType="outlined"
               @click="onChoiceClick('customise')"
            >
               <span class="px-3">{{ $t("privacy-and-terms.cookie_manager_mini.button_customise") }}</span>
            </DefaultButton>

            <!-- reject all cookies -->
            <DefaultButton
               data-cy="cookie-reject-all"
               class="ml-0 mt-4 shrink-0 grow sm:ml-2 sm:mt-0"
               size="sm"
               :color="Theme.BrandDark"
               styleType="outlined"
               @click="onChoiceClick('reject-all')"
            >
               <span class="px-3">{{ $t("privacy-and-terms.cookie_manager_mini.button_reject_all") }}</span>
            </DefaultButton>
         </div>
      </div>
   </dialog>
</template>

<script setup lang="ts">
import { useCookieSettingsStore } from "@/stores/cookieSettingsStore";
import { useI18n } from "@/vue-i18n/vue-i18n";
import { Theme, type RichText } from "@/types";

const $emit = defineEmits(["customise-choices"]);
const { t: $t } = useI18n();
const cookiesPopupHTMLElem = ref(null);
const cookieSettingsStore = useCookieSettingsStore();

// props
// -----------------------------------------
const props = defineProps<{
   description: RichText;
}>();

// lifecycle
// -----------------------------------------
onMounted(() => {
   cookiesPopupHTMLElem.value.showModal();

   // on cancel (when user presses ESC key - reject all cookies)
   cookiesPopupHTMLElem.value.addEventListener("cancel", () => {
      cookiesPopupHTMLElem.value.close();
      cookieSettingsStore.rejectAllCookies();
   });
});

// Methods
// -----------------------------------------
function onChoiceClick(selectedChoice: "accept-all" | "reject-all" | "customise") {
   // accept all the individual cookie preferences (also sets a cookie consented on and reloads the page)
   if (selectedChoice === "accept-all") {
      cookieSettingsStore.acceptAllCookies();
   }
   // reject all the individual cookie preferences (also sets a cookie consented on and reloads the page)
   else if (selectedChoice === "reject-all") {
      cookieSettingsStore.rejectAllCookies();
   }
   // customise > emit up to open the cookie settings modal
   else if (selectedChoice === "customise") {
      $emit("customise-choices");
   }
}
</script>
<style lang="scss" scoped>
dialog[open] {
   opacity: 1;
}

dialog {
   transition: opacity 0.5s;
}

@starting-style {
   dialog[open] {
      opacity: 0;
   }
}
</style>
